// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedInvoiceLineItemDeltaListProperty } from '@/audit/api/types';
import { AuditPropertyUpdateLabel } from '@/audit/components/audit-property-update-label';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { Stack, Typography } from '@mui/material';

type LineItemEntry = NonNullable<
  CachedInvoiceLineItemDeltaListProperty['newValue']
>[number];

// Mirrors the pattern in audit-project-line-items-list-label.tsx: build a
// normalized update record per line item (addition / deletion / change),
// then render a per-item block with the description as the header and
// each changed scalar as an old → new transition. Rows whose data is
// identical to the old snapshot are hidden entirely.

interface PropertyUpdate<Value> {
  oldValue: Value | null;
  newValue: Value | null;
}

type ChangeKind = 'added' | 'removed' | 'updated';

interface LineItemUpdate {
  key: string;
  name: string;
  kind: ChangeKind;
  indexPosition: PropertyUpdate<number>;
  description: PropertyUpdate<string>;
  pricingMode: PropertyUpdate<string>;
  quantity: PropertyUpdate<number>;
  unitPrice: PropertyUpdate<string>;
  taxAmount: PropertyUpdate<string>;
  totalPrice: PropertyUpdate<string>;
  servicePeriodStart: PropertyUpdate<string>;
  servicePeriodEnd: PropertyUpdate<string>;
}

interface ChangeRow {
  name: string;
  oldDisplay: string | number | null;
  newDisplay: string | number | null;
  show: boolean;
}

const PRICING_MODE_LABELS: Record<string, string> = {
  PER_UNIT: 'Per unit',
  FIXED: 'Fixed',
};

function getLabelForPricingMode(value: string | null): string | null {
  if (value === null) return null;
  return PRICING_MODE_LABELS[value.toUpperCase()] ?? value;
}

// 1-indexed display so the "#N" matches how the invoice reads to the user.
function getLabelForIndexPosition(value: number | null): string | null {
  if (value === null) return null;
  return `#${String(value + 1)}`;
}

function isPerUnit(mode: string | null): boolean {
  return mode?.toUpperCase() === 'PER_UNIT';
}

function isFixed(mode: string | null): boolean {
  return mode?.toUpperCase() === 'FIXED';
}

// When the pricing mode flips between FIXED and PER_UNIT, the meaning of
// the unit price and amount swap on each side of the transition. On the
// FIXED side the unit price is intentionally unset (rendered "None") and
// the amount is the explicit decimal. On the PER_UNIT side the unit price
// carries the per-unit value and the amount is computed from qty × unit
// price. When no flip is happening, both sides use the standard currency
// formatting.

// unitPrice / totalPrice already arrive as formatted currency strings from
// auditCurrencyValueSchema, so these helpers pass the value through rather
// than re-running formatCurrency (which would double-format the symbol).
function formatUnitPriceDisplay(
  mode: string | null,
  unitPrice: string | null,
  isPricingModeFlip: boolean
): string | null {
  // On the FIXED side of a flip, the unit price is conceptually absent.
  // Rendering null lets AuditPropertyValue show "None".
  if (isPricingModeFlip && isFixed(mode)) {
    return null;
  }
  return unitPrice;
}

function formatAmountDisplay(totalPrice: string | null): string | null {
  return totalPrice;
}

function headerName(entry: LineItemEntry): string {
  // Prefer the line item description (user-facing); fall back to the item
  // name so the audit doesn't render a blank header if a description is
  // ever cleared.
  return entry.description || entry.itemContext.name || 'Line item';
}

function prop<V>(oldValue: V | null, newValue: V | null): PropertyUpdate<V> {
  return { oldValue, newValue };
}

function buildLineItemUpdate(
  oldEntry: LineItemEntry | undefined,
  newEntry: LineItemEntry | undefined
): LineItemUpdate {
  const anchor = newEntry ?? oldEntry;
  // One of the two must be defined — the caller guarantees this.
  const key = anchor!.lineItemKey;
  const name = headerName(anchor!);

  let kind: ChangeKind = 'updated';
  if (oldEntry === undefined) kind = 'added';
  else if (newEntry === undefined) kind = 'removed';

  return {
    key,
    name,
    kind,
    indexPosition: prop(
      oldEntry?.indexPosition ?? null,
      newEntry?.indexPosition ?? null
    ),
    description: prop(
      oldEntry?.description ?? null,
      newEntry?.description ?? null
    ),
    pricingMode: prop(
      oldEntry?.pricingMode ?? null,
      newEntry?.pricingMode ?? null
    ),
    quantity: prop(oldEntry?.quantity ?? null, newEntry?.quantity ?? null),
    unitPrice: prop(oldEntry?.unitPrice ?? null, newEntry?.unitPrice ?? null),
    taxAmount: prop(oldEntry?.taxAmount ?? null, newEntry?.taxAmount ?? null),
    totalPrice: prop(
      oldEntry?.totalPrice ?? null,
      newEntry?.totalPrice ?? null
    ),
    servicePeriodStart: prop(
      oldEntry?.servicePeriodStart ?? null,
      newEntry?.servicePeriodStart ?? null
    ),
    servicePeriodEnd: prop(
      oldEntry?.servicePeriodEnd ?? null,
      newEntry?.servicePeriodEnd ?? null
    ),
  };
}

// Determines whether a field should appear in the audit output. For additions
// a field shows if it has a new value; for removals if it had an old value;
// for updates only if the value actually changed (or forceShow overrides).
function shouldShow(
  kind: ChangeKind,
  oldValue: string | number | null,
  newValue: string | number | null,
  forceShow = false
): boolean {
  if (kind === 'added') return newValue !== null;
  if (kind === 'removed') return oldValue !== null;
  return forceShow || oldValue !== newValue;
}

// Creates a ChangeRow from raw old/new values, applying an optional display
// formatter and the standard visibility rule.
function simpleRow<V extends string | number | null>(
  kind: ChangeKind,
  name: string,
  field: PropertyUpdate<V>,
  format?: (v: V) => string | number | null
): ChangeRow {
  const fmt = format ?? ((v: V) => v);
  const oldDisplay = fmt(field.oldValue as V);
  const newDisplay = fmt(field.newValue as V);
  return {
    name,
    oldDisplay,
    newDisplay,
    show: shouldShow(kind, field.oldValue, field.newValue),
  };
}

// Builds the per-line-item rows used by LineItemChangeBlock. For additions
// every populated field is shown, for deletions every populated old field
// is shown, and for updates only the changed fields are shown.
//
// The pricing-mode flip case (FIXED ↔ PER_UNIT) forces unit price and
// amount to always display, because the meaning of those fields changes
// even when the raw values don't.
function buildChangeRows(update: LineItemUpdate): ChangeRow[] {
  const isPricingModeFlip =
    update.kind === 'updated' &&
    update.pricingMode.oldValue !== update.pricingMode.newValue &&
    ((isFixed(update.pricingMode.oldValue) &&
      isPerUnit(update.pricingMode.newValue)) ||
      (isPerUnit(update.pricingMode.oldValue) &&
        isFixed(update.pricingMode.newValue)));

  // --- Standard fields: format + show when changed ---
  const standardRows: ChangeRow[] = [
    simpleRow(
      update.kind,
      'Line number',
      update.indexPosition,
      getLabelForIndexPosition
    ),
    simpleRow(update.kind, 'Description', update.description),
    simpleRow(
      update.kind,
      'Pricing mode',
      update.pricingMode,
      getLabelForPricingMode
    ),
    simpleRow(update.kind, 'Quantity', update.quantity),
    simpleRow(update.kind, 'Tax amount', update.taxAmount),
    simpleRow(update.kind, 'Service period start', update.servicePeriodStart),
    simpleRow(update.kind, 'Service period end', update.servicePeriodEnd),
  ];

  // --- Pricing-sensitive fields: custom display on mode flips ---
  const unitPriceRow: ChangeRow = {
    name: 'Unit price',
    oldDisplay: formatUnitPriceDisplay(
      update.pricingMode.oldValue,
      update.unitPrice.oldValue,
      isPricingModeFlip
    ),
    newDisplay: formatUnitPriceDisplay(
      update.pricingMode.newValue,
      update.unitPrice.newValue,
      isPricingModeFlip
    ),
    show: shouldShow(
      update.kind,
      update.unitPrice.oldValue,
      update.unitPrice.newValue,
      isPricingModeFlip
    ),
  };
  const amountRow: ChangeRow = {
    name: 'Amount',
    oldDisplay: formatAmountDisplay(update.totalPrice.oldValue),
    newDisplay: formatAmountDisplay(update.totalPrice.newValue),
    show: shouldShow(
      update.kind,
      update.totalPrice.oldValue,
      update.totalPrice.newValue,
      isPricingModeFlip
    ),
  };

  // Insert unit price after quantity (index 3), amount after unit price
  return [
    ...standardRows.slice(0, 4),
    unitPriceRow,
    ...standardRows.slice(4, 5),
    amountRow,
    ...standardRows.slice(5),
  ];
}

interface LineItemChangeBlockProps {
  update: LineItemUpdate;
  rows: ChangeRow[];
}

function LineItemChangeBlock(props: LineItemChangeBlockProps) {
  const { update, rows } = props;
  return (
    <Stack key={update.key}>
      <Typography variant="caption" marginLeft={2}>
        <Typography component="span" variant="inherit">
          {update.name}
        </Typography>{' '}
        <Typography
          component="span"
          variant="inherit"
          sx={{ textDecoration: 'underline' }}
        >
          {update.kind}
        </Typography>
      </Typography>
      {rows
        .filter((row) => row.show)
        .map((row) => (
          <Typography
            key={row.name}
            variant="caption"
            component="div"
            marginLeft={4}
          >
            {update.kind === 'added' && (
              <>
                {row.name}:&nbsp;
                <AuditPropertyValue value={row.newDisplay} />
              </>
            )}
            {update.kind === 'removed' && (
              <>
                {row.name}:&nbsp;
                <AuditPropertyValue value={row.oldDisplay} />
              </>
            )}
            {update.kind === 'updated' && (
              <AuditPropertyUpdateLabel
                name={row.name}
                oldValue={row.oldDisplay}
                newValue={row.newDisplay}
              />
            )}
          </Typography>
        ))}
    </Stack>
  );
}

interface Props {
  property: CachedInvoiceLineItemDeltaListProperty;
}

export function AuditInvoiceLineItemDeltaDetails(props: Props) {
  const oldEntries = props.property.oldValue ?? [];
  const newEntries = props.property.newValue ?? [];

  // Index both sides by lineItemKey so the pairing is a single lookup
  // instead of nested finds.
  const oldByKey = new Map(
    oldEntries.map((entry) => [entry.lineItemKey, entry])
  );
  const newByKey = new Map(
    newEntries.map((entry) => [entry.lineItemKey, entry])
  );

  const updates: LineItemUpdate[] = [];

  // Walk old first to capture deletions and changes.
  oldEntries.forEach((oldEntry) => {
    const newEntry = newByKey.get(oldEntry.lineItemKey);
    updates.push(buildLineItemUpdate(oldEntry, newEntry));
  });

  // Append additions (new keys not present in old).
  newEntries.forEach((newEntry) => {
    if (oldByKey.has(newEntry.lineItemKey)) return;
    updates.push(buildLineItemUpdate(undefined, newEntry));
  });

  // Compute the visible-row list per update first so the JSX map is a
  // straight render with no counters mutating during render.
  const visibleUpdates = updates
    .map((update) => ({ update, rows: buildChangeRows(update) }))
    .filter(({ rows }) => rows.some((row) => row.show));

  if (visibleUpdates.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {visibleUpdates.map(({ update, rows }) => (
        <LineItemChangeBlock key={update.key} update={update} rows={rows} />
      ))}
    </Stack>
  );
}
